import React, { useState, useEffect, useRef } from 'react';
import { convertNumberToWords, convertNumberToWordsTaka, formatNumberWithCommas, billLineNet, billVatTotals } from '../utilities';
import axios from '../axios';
import { useParams } from 'react-router-dom';

const BillPDF = () => {
  const { billId } = useParams();
  const [bill_challans, set_bill_challans] = useState([]);
  const [bill_info, set_bill_info] = useState({});
  const [loading, setLoading] = useState(true);
  const [companyTitle, setCompanyTitle] = useState(localStorage.getItem('company'));

  const [grandTotalQty, setGrandTotalQty] = useState(0);
  const [lineValues, setLineValues] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fontScale, setFontScale] = useState(100); // percent, 70–120
  const [rowSpacing, setRowSpacing] = useState(100); // percent, 40–100
  const [pendingSave, setPendingSave] = useState(false);
  const printableRef = useRef(null);

  // Generic contenteditable sweep — same technique as PiPDF/GroupPiPDF/SavedCommercialViewer.
  useEffect(() => {
    if (!printableRef.current) return;
    const nodes = printableRef.current.querySelectorAll('td, th, span, strong, h1, h2, div');
    nodes.forEach((el) => {
      if (
        el.hasAttribute('contenteditable') ||
        el.children.length === 0 ||
        el.tagName === 'TD' ||
        el.tagName === 'TH'
      ) {
        el.setAttribute('contenteditable', editMode ? 'true' : 'false');
      }
    });
  }, [editMode, loading]);

  const handleSaveClick = () => {
    if (editMode) {
      setEditMode(false);
      setPendingSave(true);
    } else {
      captureAndSave();
    }
  };

  const captureAndSave = async () => {
    if (!printableRef.current) return;
    setSaving(true);
    const htmlContent = printableRef.current.outerHTML;
    try {
      await axios.post(`${window.backendUrl}/save_bill_document/${billId}`, { htmlContent });
    } catch (err) {
      console.log(err);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (pendingSave && !editMode) {
      captureAndSave();
      setPendingSave(false);
    }
  }, [editMode, pendingSave]);

  const decreaseFont = () => setFontScale((prev) => Math.max(70, prev - 5));
  const increaseFont = () => setFontScale((prev) => Math.min(120, prev + 5));
  const decreaseSpacing = () => setRowSpacing((prev) => Math.max(40, prev - 10));
  const increaseSpacing = () => setRowSpacing((prev) => Math.min(100, prev + 10));

  useEffect(() => {
    const storedCompany = localStorage.getItem('company');
    if (storedCompany) {
      setCompanyTitle(storedCompany);
    }
  }, []);

  useEffect(() => {
    if (billId) {
      fetch_bill_pdf_info();
    }
  }, [billId]);

  const fetch_bill_pdf_info = () => {
    axios.get(`${window.backendUrl}/get_bill_pdf_info/${billId}`)
      .then((response) => {
        const challans = response.data.billDetails;
        set_bill_challans(challans);
        set_bill_info(response.data.billInfo);

        // Calculate Totals by traversing the tree
        let qty = 0;
        const values = [];
        challans.forEach(challan => {
          challan.items.forEach(item => {
            item.specifications.forEach(spec => {
              spec.gsms.forEach(gsm => {
                qty += gsm.deliveryQty;
                values.push(gsm.totalAmount);
              });
            });
          });
        });
        setGrandTotalQty(qty);
        setLineValues(values);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  // The tab title is what the browser prints in the page header, so it must
  // name the bill's company, not the app's default "MIAH & SONS" title.
  useEffect(() => {
    const previousTitle = document.title;
    document.title = bill_info.BillNo ? `${companyTitle} - Bill ${bill_info.BillNo}` : companyTitle || previousTitle;
    return () => { document.title = previousTitle; };
  }, [companyTitle, bill_info.BillNo]);

  if (loading) return <></>;

  let slCounter = 0;

  // 15% VAT, only when the bill has the VAT tick (same rule and rounding as the bill list and the Tally voucher).
  // "Price includes VAT": the prices already hold the 15%, so lines show the price without VAT
  // (e.g. 84.90 -> 73.83) and the grand total stays the sum of the original prices.
  const hasVat = !!bill_info.HasVat;
  const vatInclusive = hasVat && !!bill_info.VatInclusive;
  // Bills of USD orders: lines in $. With the bill's exchange rate each line is converted to Taka (rounded to 2)
  // and the totals, VAT and amount in words are in Taka - the same figures as the Tally voucher.
  const usd = bill_info.Currency === '$';
  const sym = usd ? '$' : '৳';
  const exRate = usd && Number(bill_info.ExchangeRate) > 0 ? Number(bill_info.ExchangeRate) : null;
  const usdTotal = billVatTotals(lineValues, false, false).total;
  const totalValues = exRate ? lineValues.map((v) => Number((v * exRate).toFixed(2))) : lineValues;
  const { net: netAmount, vat: vatAmount, total: payableTotal } = billVatTotals(totalValues, hasVat, vatInclusive);
  const totalSym = exRate ? '৳' : sym;
  const shownUnitPrice = (price) => (vatInclusive ? Number(price) / 1.15 : Number(price));
  const styles = bill_info.Styles || [];
  const orderNos = bill_info.OrderNos || [];

  // Letterhead per company — Propack follows its printed pad (Propack Pad.pdf).
  // Tanvir has no logo, so its name doubles as the watermark.
  const letterhead = companyTitle === 'Propack Trims & Accessories Industries'
    ? {
        logo: '/Images/propacklogo.png',
        line1: 'PROPACK TRIMS &',
        line2: 'ACCESSORIES INDUSTRIES',
        color1: '#7a151a',
        color2: '#eb1760',
        address: 'Muradpur, Shakaswar, Kaliakoir, Gazipur-1703, Bangladesh.',
      }
    : companyTitle === 'Tanvir Printing & Packaging IND.'
      ? {
          logo: null,
          line1: 'TANVIR PRINTING &',
          line2: 'PACKAGING IND.',
          color1: '#0b2e8a',
          color2: '#1f5fd1',
          address: '746/747 NOLVOG, DHORANGERTEK, TURAG, UTTARA, DHAKA-1230',
        }
      : {
          logo: '/Images/miahandsonslogo.png',
          line1: 'MIAH & SONS',
          line2: 'INDUSTRIES LTD.',
          color1: '#f36f26',
          color2: '#6d6e71',
          address: 'Holding No: 110/01, Mouza: Barabo, PO. Shakaswar, P.S. Kaliakoir, Gazipur-1703, Bangladesh.',
        };

  // Row spacing (density) — same mechanism as PiPDF/GroupPiPDF/CommercialPDF.
  // Header/body cells here pad themselves directly with 5px, uniformly, so the
  // base is a single value. At 100% no CSS is emitted, so default output is
  // unchanged.
  const spacingFactor = rowSpacing / 100;
  const spacingLineHeight = (1.15 + ((rowSpacing - 40) / 60) * 0.35).toFixed(2);
  const rowSpacingCss =
    rowSpacing < 100
      ? `
        .bill-item-table th, .bill-item-table td {
          padding-top: calc(5px * ${spacingFactor}) !important;
          padding-bottom: calc(5px * ${spacingFactor}) !important;
          line-height: ${spacingLineHeight} !important;
        }
      `
      : '';

  return (
    <>
      <div className="no-print" style={{ margin: '8px 8px 0', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <button onClick={() => setEditMode((prev) => !prev)}>
          {editMode ? 'Normal View' : 'Edit Content'}
        </button>
        <button onClick={handleSaveClick} disabled={saving}>
          {saving ? 'Saving...' : 'Save Document'}
        </button>
        <span style={{ marginLeft: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <button type="button" onClick={decreaseFont} disabled={fontScale <= 70} title="Decrease font size">A−</button>
          <span style={{ minWidth: '48px', textAlign: 'center', fontSize: '13px' }}>{fontScale}%</span>
          <button type="button" onClick={increaseFont} disabled={fontScale >= 120} title="Increase font size">A+</button>
        </span>
        <span style={{ marginLeft: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '13px' }}>Row spacing</span>
          <button type="button" onClick={decreaseSpacing} disabled={rowSpacing <= 40} title="Make table rows more compact">−</button>
          <span style={{ minWidth: '48px', textAlign: 'center', fontSize: '13px' }}>{rowSpacing}%</span>
          <button type="button" onClick={increaseSpacing} disabled={rowSpacing >= 100} title="Make table rows taller">+</button>
        </span>
      </div>
      <style>{`
        .bill-watermark {
          position: absolute; top: 320px; left: 0; right: 0;
          opacity: 0.08; pointer-events: none; z-index: 2; text-align: center;
        }
        .bill-watermark img { mix-blend-mode: multiply; }
        @media print {
          .no-print { display: none !important; }
          [contenteditable] { outline: none !important; background-color: transparent !important; }
          .bill-watermark { position: fixed; top: 50%; transform: translateY(-50%); }
        }
      `}</style>
    <div ref={printableRef} style={{ width: '100%', zoom: fontScale / 100, position: 'relative' }}>
    {rowSpacingCss && <style>{rowSpacingCss}</style>}
      {/* Faded company mark behind the bill, like the printed letterhead pad */}
      <div className="bill-watermark">
        {letterhead.logo
          ? <img src={letterhead.logo} alt="" style={{ width: '360px' }} />
          : <div style={{ fontSize: '44px', fontWeight: 'bold', color: letterhead.color1, lineHeight: 1.2, whiteSpace: 'nowrap' }}>{letterhead.line1}<br />{letterhead.line2}</div>}
      </div>
      <table style={{ width: '100%', padding: 0, borderSpacing: 1, position: 'relative', zIndex: 1 }}>
        <tbody>
          {/* --- HEADER --- */}
          <tr>
            <td>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    {letterhead.logo && (
                      <td style={{ width: '150px', textAlign: 'center', verticalAlign: 'middle' }}>
                        <img style={{ maxHeight: '75px', maxWidth: '140px' }} src={letterhead.logo} alt={companyTitle} />
                      </td>
                    )}
                    <td style={{ textAlign: letterhead.logo ? 'left' : 'center', verticalAlign: 'middle' }}>
                      <div style={{ fontSize: '30px', fontWeight: 800, color: letterhead.color1, lineHeight: 1.1, letterSpacing: '1px' }}>{letterhead.line1}</div>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: letterhead.color2, letterSpacing: '6px', marginTop: '4px' }}>{letterhead.line2}</div>
                      <div style={{ fontSize: '12px', lineHeight: '18px', marginTop: '6px' }}>
                        <strong>Factory Address:</strong> {letterhead.address}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style={{ borderTop: `2px solid ${letterhead.color1}`, marginTop: '8px' }} />
              <div style={{ textAlign: 'center' }}>
                <h2 className='challan_header' style={{ display: 'inline-block', border: '1px solid #333', fontSize: '20px', padding: '5px 16px', margin: '10px 0 0' }}>Bill</h2>
              </div>
            </td>
          </tr>

          {/* --- BILL INFO --- */}
          <tr>
            <td>
              <table style={{ width: '100%', fontSize: '12px', margin: "20px 0" }}>
                <tbody>
                  <tr>
                    <td className='bill_td' valign="top" style={{ width: '50%', verticalAlign: 'top' }}>
                        <strong>Bill No. :</strong> {bill_info.BillNo}<br/>
                        <strong>Bill Date :</strong> {new Date(bill_info.BillDate * 1000).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.')}
                        {styles.length > 0 && <><br/><strong>Style :</strong> {styles.join(', ')}</>}
                        {orderNos.length > 0 && <><br/><strong>Order No. :</strong> {orderNos.join(', ')}</>}
                    </td>
                    <td className='bill_td' valign="top" style={{ width: '50%', verticalAlign: 'top' }}>
                        <strong>Party Name :</strong> {bill_info.PartyName}<br/>
                        <strong>Address :</strong> {bill_info.PartyAddress}<br/>
                        {bill_info.ChallanPartyName && <><strong>Challan Party :</strong> {bill_info.ChallanPartyName}<br/></>}
                        <strong>Buyer :</strong> {bill_info.BuyerName}
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>

          {/* --- MAIN TABLE --- */}
          <tr>
            <td>
              <table className="bill-item-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ fontSize: '12px' }}>
                  <tr>
                    <th style={{ border: '1px solid black', padding: '5px', backgroundColor: '#f0f0f0' }}>SL</th>
                    <th style={{ border: '1px solid black', padding: '5px', backgroundColor: '#f0f0f0' }}>Challan No</th>
                    <th style={{ border: '1px solid black', padding: '5px', backgroundColor: '#f0f0f0' }}>Item Name</th>
                    <th style={{ border: '1px solid black', padding: '5px', backgroundColor: '#f0f0f0' }}>Specification</th>
                    <th style={{ border: '1px solid black', padding: '5px', backgroundColor: '#f0f0f0' }}>Measurement</th>
                    <th style={{ border: '1px solid black', padding: '5px', backgroundColor: '#f0f0f0' }}>Delivery Qty</th>
                    <th style={{ border: '1px solid black', padding: '5px', backgroundColor: '#f0f0f0' }}>Unit Price</th>
                    <th style={{ border: '1px solid black', padding: '5px', backgroundColor: '#f0f0f0' }}>Total Amount</th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: '12px' }}>
                  {bill_challans.map((challan, cIndex) => {
                    // SL number increments per Challan
                    slCounter++;
                    
                    return challan.items.map((item, iIndex) => {
                      return item.specifications.map((spec, sIndex) => {
                        return spec.gsms.map((gsm, gIndex) => {
                          return (
                            <tr key={`${cIndex}-${iIndex}-${sIndex}-${gIndex}`}>
                              
                              {/* 1. SL & Challan No (Spans entire Challan) */}
                              {iIndex === 0 && sIndex === 0 && gIndex === 0 && (
                                <>
                                  <td rowSpan={challan.totalRows} style={{ border: '1px solid black', textAlign: 'center', verticalAlign: 'middle' }}>
                                    {slCounter}
                                  </td>
                                  <td rowSpan={challan.totalRows} style={{ border: '1px solid black', textAlign: 'center', verticalAlign: 'middle' }}>
                                    {challan.challanNo}
                                  </td>
                                </>
                              )}

                              {/* 2. Item Name (Spans entire Item) */}
                              {sIndex === 0 && gIndex === 0 && (
                                <td rowSpan={item.totalRows} style={{ border: '1px solid black', textAlign: 'center', verticalAlign: 'middle' }}>
                                  {item.itemName}
                                </td>
                              )}

                              {/* 3. Specification (Spans entire Spec) */}
                              {gIndex === 0 && (
                                <td rowSpan={spec.gsms.length} style={{ border: '1px solid black', textAlign: 'center', verticalAlign: 'middle' }}>
                                  {spec.specification}
                                </td>
                              )}

                              {/* 4. GSM Data (One row per GSM) */}
                              <td style={{ border: '1px solid black', textAlign: 'center', padding: '5px' }}>
                                {gsm.measurement}
                              </td>
                              <td style={{ border: '1px solid black', textAlign: 'center', padding: '5px' }}>
                                {gsm.deliveryQty}
                              </td>
                              <td style={{ border: '1px solid black', textAlign: 'right', padding: '5px' }}>
                                {sym}{formatNumberWithCommas(shownUnitPrice(gsm.unitPrice).toFixed(2))}
                              </td>
                              <td style={{ border: '1px solid black', textAlign: 'right', padding: '5px' }}>
                                {sym}{formatNumberWithCommas(billLineNet(gsm.totalAmount, vatInclusive).toFixed(2))}
                              </td>

                            </tr>
                          );
                        });
                      });
                    });
                  })}

                  {/* USD bill with a rate: the $ total, then everything below in Taka */}
                  {exRate && (
                    <>
                      <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                        <td colSpan={5} style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>Total (USD):</td>
                        <td style={{ border: '1px solid black', padding: '8px', textAlign: 'center' }}>{grandTotalQty}</td>
                        <td style={{ border: '1px solid black', padding: '8px' }}></td>
                        <td style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>${formatNumberWithCommas(usdTotal.toFixed(2))}</td>
                      </tr>
                      <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                        <td colSpan={7} style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>Exchange Rate:</td>
                        <td style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>Tk {formatNumberWithCommas(exRate.toFixed(2))} per $</td>
                      </tr>
                    </>
                  )}
                  {/* TOTAL ROW */}
                  {hasVat ? (
                    <>
                      <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                        <td colSpan={5} style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>{exRate ? 'Total (Taka):' : 'Total:'}</td>
                        <td style={{ border: '1px solid black', padding: '8px', textAlign: 'center' }}>{grandTotalQty}</td>
                        <td style={{ border: '1px solid black', padding: '8px' }}></td>
                        <td style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>{totalSym}{formatNumberWithCommas(netAmount.toFixed(2))}</td>
                      </tr>
                      <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                        <td colSpan={7} style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>VAT 15%{bill_info.VatChallanNo ? ` (Vat Challan No: ${bill_info.VatChallanNo})` : ''}:</td>
                        <td style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>{totalSym}{formatNumberWithCommas(vatAmount.toFixed(2))}</td>
                      </tr>
                      <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                        <td colSpan={7} style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>Grand Total (incl. VAT):</td>
                        <td style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>{totalSym}{formatNumberWithCommas(payableTotal.toFixed(2))}</td>
                      </tr>
                    </>
                  ) : (
                    <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                      <td colSpan={5} style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>{exRate ? 'Grand Total (Taka):' : 'Grand Total:'}</td>
                      <td style={{ border: '1px solid black', padding: '8px', textAlign: 'center' }}>{grandTotalQty}</td>
                      <td style={{ border: '1px solid black', padding: '8px' }}></td>
                      <td style={{ border: '1px solid black', padding: '8px', textAlign: 'right' }}>{totalSym}{formatNumberWithCommas(netAmount.toFixed(2))}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>

          {/* --- IN WORDS & FOOTER --- */}
          <tr>
            <td>
               <div style={{ marginTop: '15px', fontSize: '12px', border: '1px solid black', padding: '5px' }}>
                 <strong>{usd && !exRate ? `In Words (USD): ${convertNumberToWords(Number(payableTotal.toFixed(2)))} Only.` : `In Words (BDT): ${convertNumberToWordsTaka(payableTotal.toFixed(2))} Only.`}</strong>
               </div>
            </td>
          </tr>
          {/* --- AUTHORISED SIGNATURE (same block as the PI) --- */}
          <tr>
            <td>
              <table style={{ marginTop: '18px' }} width="100%" border="0" cellSpacing="0" cellPadding="0">
                <tbody>
                  <tr>
                    <td width="50%"></td>
                    <td width="50%" align="center">
                      <strong style={{ fontSize: '15px' }}>{companyTitle}</strong>
                      <br />
                      <img style={{ maxWidth: '150px', mixBlendMode: 'multiply' }} src="/Images/miah_and_sons_signature.png" alt="AUTHORISED SIGNATURE" />
                      <br />
                      <span style={{ borderTop: '1px solid black', fontSize: '12px' }}>AUTHORISED SIGNATURE</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    </>
  );
};

export default BillPDF;
